import React, { useMemo } from 'react';
import { Table, Tabs, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { APDetailRow, APSummaryRow, QueryResponse } from '../../../services/h301Api';

interface ResultTableProps {
  result: QueryResponse;
  reportType: string;
}

const fmtAmt = (v: number): string => {
  if (v == null || v === 0) return '';
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const detailColumns: ColumnsType<APDetailRow & { _key: string }> = [
  {
    title: '供應商',
    dataIndex: 'supplierCode',
    key: 'supplierCode',
    width: 100,
    render: (code: string, record) =>
      `${(code || '').trim()} ${(record.supplierName || '').trim()}`,
  },
  {
    title: '部門',
    dataIndex: 'deptCode',
    key: 'deptCode',
    width: 80,
    render: (code: string, record) =>
      `${(code || '').trim()} ${(record.deptName || '').trim()}`,
  },
  {
    title: '採購員',
    dataIndex: 'buyerCode',
    key: 'buyerCode',
    width: 80,
    render: (code: string, record) =>
      `${(code || '').trim()} ${(record.buyerName || '').trim()}`,
  },
  {
    title: '入庫單號',
    dataIndex: 'entryNo',
    key: 'entryNo',
    width: 110,
    render: (v: string) => {
      const s = (v || '').trim();
      if (s.length >= 8) {
        return `${s.substring(0, 6)}-${s.substring(6, 7)}-${s.substring(7)}`;
      }
      return s;
    },
  },
  {
    title: '入庫日期',
    dataIndex: 'entryDate',
    key: 'entryDate',
    width: 100,
  },
  {
    title: '應付金額',
    dataIndex: 'amountDue',
    key: 'amountDue',
    width: 110,
    align: 'right' as const,
    render: (v: number) => fmtAmt(v),
  },
  {
    title: '',
    dataIndex: 'markFlag',
    key: 'markFlag',
    width: 30,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '單據號碼',
    dataIndex: 'docNo',
    key: 'docNo',
    width: 100,
  },
  {
    title: '類別',
    dataIndex: 'docTypeName',
    key: 'docTypeName',
    width: 80,
  },
  {
    title: '沖銷金額',
    dataIndex: 'payAmount',
    key: 'payAmount',
    width: 110,
    align: 'right' as const,
    render: (v: number) => fmtAmt(v),
  },
  {
    title: '應付餘額',
    dataIndex: 'balance',
    key: 'balance',
    width: 110,
    align: 'right' as const,
    render: (v: number) => {
      const formatted = fmtAmt(v);
      if (!formatted) return '';
      const color = v < 0 ? 'red' : undefined;
      return <span style={{ color }}>{formatted}</span>;
    },
  },
];

const memoColumns: ColumnsType<APDetailRow & { _key: string }> = [
  ...detailColumns.filter(c => c.key !== 'markFlag'),
  {
    title: '異常說明',
    dataIndex: 'memo',
    key: 'memo',
    width: 200,
    ellipsis: true,
  },
];

const summaryColumns: ColumnsType<APSummaryRow & { _key: string }> = [
  {
    title: '分組',
    dataIndex: 'groupKey',
    key: 'groupKey',
    width: 100,
    render: (code: string, record) =>
      `${(code || '').trim()} ${(record.groupName || '').trim()}`,
  },
  {
    title: '科目',
    dataIndex: 'accountCode',
    key: 'accountCode',
    width: 60,
  },
  {
    title: '應付總額',
    dataIndex: 'totalDue',
    key: 'totalDue',
    width: 120,
    align: 'right' as const,
    render: (v: number) => fmtAmt(v),
  },
  {
    title: '已付總額',
    dataIndex: 'totalPaid',
    key: 'totalPaid',
    width: 120,
    align: 'right' as const,
    render: (v: number) => fmtAmt(v),
  },
  {
    title: '餘額',
    key: 'totalBalance',
    dataIndex: 'totalBalance',
    width: 120,
    align: 'right' as const,
    render: (v: number) => {
      const formatted = fmtAmt(v);
      if (!formatted) return '';
      const color = v > 0 ? 'red' : v < 0 ? 'green' : undefined;
      return <Tag color={color}>{formatted}</Tag>;
    },
  },
];

const ResultTable: React.FC<ResultTableProps> = ({ result, reportType }) => {
  const detailData = useMemo(
    () =>
      (result.details || []).map((row, i) => ({
        ...row,
        _key: `${row.supplierCode}-${row.entryNo}-${row.docNo}-${i}`,
      })),
    [result.details]
  );

  const summaryData = useMemo(
    () =>
      (result.summaries || []).map((row, i) => ({
        ...row,
        _key: `${row.groupKey}-${row.accountCode}-${i}`,
      })),
    [result.summaries]
  );

  const cols = reportType === 'memo' ? memoColumns : detailColumns;

  const items = [
    {
      key: 'details',
      label: `應付明細 (${result.totalCount})`,
      children: (
        <Table
          columns={cols}
          dataSource={detailData}
          rowKey="_key"
          size="small"
          scroll={{ x: 1200, y: 500 }}
          pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
        />
      ),
    },
    {
      key: 'summary',
      label: `分組彙總 (${summaryData.length})`,
      children: (
        <Table
          columns={summaryColumns}
          dataSource={summaryData}
          rowKey="_key"
          size="small"
          scroll={{ x: 600, y: 500 }}
          pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
        />
      ),
    },
  ];

  return <Tabs items={items} />;
};

export default ResultTable;
